import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { partsKeys } from '../parts/queries';
import { partStorageApi, uploadFileWithProgress } from './service';
import type { PartDocumentKind } from './types';

export const partStorageKeys = {
  all: ['part-storage'] as const,
  photoUrl: (partId: string) =>
    [...partStorageKeys.all, 'photo-url', partId] as const,
  documents: (partId: string, kind?: PartDocumentKind) =>
    [
      ...partStorageKeys.all,
      'documents',
      partId,
      kind ?? null,
    ] as const,
};

const PART_ASSETS_CACHE = 'part-assets';

async function dropPwaCacheEntry(url: string): Promise<void> {
  if (typeof caches === 'undefined') return;
  try {
    const cache = await caches.open(PART_ASSETS_CACHE);
    await cache.delete(url.split('?')[0]);
  } catch {
    // No SW running (dev without PWA, tests) — ignore.
  }
}

export function usePartPhotoUrl(partId: string | undefined) {
  return useQuery({
    queryKey: partId
      ? partStorageKeys.photoUrl(partId)
      : partStorageKeys.all,
    queryFn: () => partStorageApi.getPhotoUrl(partId as string),
    enabled: Boolean(partId),
    staleTime: 10 * 60 * 1000,
  });
}

export function usePartDocuments(
  partId: string | undefined,
  kind?: PartDocumentKind,
) {
  return useQuery({
    queryKey: partId
      ? partStorageKeys.documents(partId, kind)
      : partStorageKeys.all,
    queryFn: () => partStorageApi.listDocuments(partId as string, { kind }),
    enabled: Boolean(partId),
  });
}

export function useUploadPartPhoto(partId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      file: File;
      onProgress?: (loaded: number, total: number) => void;
      signal?: AbortSignal;
    }) => {
      const presigned = await partStorageApi.presignPhotoUpload(partId, {
        contentType: input.file.type,
        contentLength: input.file.size,
        fileName: input.file.name,
      });
      await uploadFileWithProgress({
        url: presigned.url,
        file: input.file,
        contentType: input.file.type,
        onProgress: input.onProgress,
        signal: input.signal,
      });
      return partStorageApi.confirmPhotoUpload(partId, {
        fileKey: presigned.fileKey,
        contentType: input.file.type,
        sizeBytes: input.file.size,
      });
    },
    onSuccess: async () => {
      if (typeof caches !== 'undefined') {
        try {
          await caches.delete(PART_ASSETS_CACHE);
        } catch {
          // ignore
        }
      }
      qc.invalidateQueries({ queryKey: partStorageKeys.photoUrl(partId) });
      qc.invalidateQueries({ queryKey: partsKeys.detail(partId) });
    },
  });
}

export function useDeletePartPhoto(partId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => partStorageApi.deletePhoto(partId),
    onSuccess: async () => {
      const url = qc.getQueryData<{ url: string } | null>(
        partStorageKeys.photoUrl(partId),
      );
      if (url?.url) await dropPwaCacheEntry(url.url);
      qc.invalidateQueries({ queryKey: partStorageKeys.photoUrl(partId) });
      qc.invalidateQueries({ queryKey: partsKeys.detail(partId) });
    },
  });
}

export function useUploadPartDocument(partId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      file: File;
      kind: PartDocumentKind;
      onProgress?: (loaded: number, total: number) => void;
      signal?: AbortSignal;
    }) => {
      const presigned = await partStorageApi.presignDocumentUpload(partId, {
        contentType: input.file.type,
        contentLength: input.file.size,
        fileName: input.file.name,
      });
      await uploadFileWithProgress({
        url: presigned.url,
        file: input.file,
        contentType: input.file.type,
        onProgress: input.onProgress,
        signal: input.signal,
      });
      return partStorageApi.createDocument(partId, {
        kind: input.kind,
        fileKey: presigned.fileKey,
        fileName: input.file.name,
        contentType: input.file.type,
        sizeBytes: input.file.size,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: [...partStorageKeys.all, 'documents', partId],
      });
    },
  });
}

export function useDeletePartDocument(partId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (docId: string) =>
      partStorageApi.deleteDocument(partId, docId),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: [...partStorageKeys.all, 'documents', partId],
      });
    },
  });
}
